import Dexie, { Table } from 'dexie';
import { CatchLog, FishingSession, FishingArea, FishingMethod, Tackle } from '../entities/FishingEntities';
import { Species } from '../entities/SpeciesEntities';
import { SavedFilter } from '../entities/SavedFilter';
import { DEFAULT_SPECIES_METADATA } from '@fishing-app/shared';

// Pending weather update for offline catches
export interface PendingWeatherUpdate {
  id: string;
  catchId: string;
  lat: number;
  lng: number;
  catchTimestamp: number; // When the catch was made (for historical weather)
  species?: string;
  depth?: 'shallow' | 'medium' | 'deep';
  queuedAt: number; // When this was queued (for cleanup/monitoring)
}

// Pending location name update for offline fishing areas
export interface PendingLocationUpdate {
  id: string;
  areaId: string;
  lat: number;
  lng: number;
  queuedAt: number;
}

// Dexie database - super enkelt för browser!
class FishingDatabase extends Dexie {
  catches!: Table<CatchLog, string>;
  sessions!: Table<FishingSession, string>;
  areas!: Table<FishingArea, string>;
  species!: Table<Species, string>;
  methods!: Table<FishingMethod, string>;
  tackle!: Table<Tackle, string>;
  savedFilters!: Table<SavedFilter, string>;
  pendingWeatherUpdates!: Table<PendingWeatherUpdate, string>;
  pendingLocationUpdates!: Table<PendingLocationUpdate, string>;

  constructor() {
    super('fishing_db');
    
    // Define schema
    this.version(1).stores({
      catches: 'id, timestamp, species, caught, sessionId, fishingAreaId, lat, lng',
      sessions: 'id, startTime, endTime, isActive',
      areas: 'id, centerLat, centerLng, lastVisited, totalCatches'
    });

    // Version 2: Add species table
    this.version(2).stores({
      catches: 'id, timestamp, species, caught, sessionId, fishingAreaId, lat, lng',
      sessions: 'id, startTime, endTime, isActive',
      areas: 'id, centerLat, centerLng, lastVisited, totalCatches',
      species: 'id, key, isCustom'
    });
    // NOTE: Species population moved to ensureDefaultSpecies() in App.tsx
    // to avoid duplicates when database is recreated

    // Version 3: Add pendingWeatherUpdates table for offline sync
    this.version(3).stores({
      catches: 'id, timestamp, species, caught, sessionId, fishingAreaId, lat, lng',
      sessions: 'id, startTime, endTime, isActive',
      areas: 'id, centerLat, centerLng, lastVisited, totalCatches',
      species: 'id, key, isCustom',
      pendingWeatherUpdates: 'id, catchId, timestamp'
    });

    // Version 4: Update pendingWeatherUpdates with catchTimestamp
    this.version(4).stores({
      catches: 'id, timestamp, species, caught, sessionId, fishingAreaId, lat, lng',
      sessions: 'id, startTime, endTime, isActive',
      areas: 'id, centerLat, centerLng, lastVisited, totalCatches',
      species: 'id, key, isCustom',
      pendingWeatherUpdates: 'id, catchId, catchTimestamp, queuedAt'
    }).upgrade(async (tx) => {
      // Migrate old timestamp field to queuedAt
      const updates = tx.table<PendingWeatherUpdate, string>('pendingWeatherUpdates');
      const items = await updates.toArray();
      for (const item of items) {
        // For old items, use timestamp as both catchTimestamp and queuedAt
        await updates.update(item.id, {
          catchTimestamp: (item as any).timestamp || item.queuedAt,
          queuedAt: (item as any).timestamp || Date.now()
        });
      }
      console.log('✅ Migrated pendingWeatherUpdates schema');
    });

    // Version 5: Add pendingLocationUpdates table for offline geocoding
    this.version(5).stores({
      catches: 'id, timestamp, species, caught, sessionId, fishingAreaId, lat, lng',
      sessions: 'id, startTime, endTime, isActive',
      areas: 'id, centerLat, centerLng, lastVisited, totalCatches',
      species: 'id, key, isCustom',
      pendingWeatherUpdates: 'id, catchId, catchTimestamp, queuedAt',
      pendingLocationUpdates: 'id, areaId, queuedAt'
    });

    // Version 6: Add methods and tackle tables for user-configurable fishing preferences
    this.version(6).stores({
      catches: 'id, timestamp, species, caught, sessionId, fishingAreaId, lat, lng',
      sessions: 'id, startTime, endTime, isActive',
      areas: 'id, centerLat, centerLng, lastVisited, totalCatches',
      species: 'id, key, isCustom',
      methods: 'id, key, isCustom, createdAt',
      tackle: 'id, key, isCustom, createdAt',
      pendingWeatherUpdates: 'id, catchId, catchTimestamp, queuedAt',
      pendingLocationUpdates: 'id, areaId, queuedAt'
    });

    // Version 7: Add savedFilters table for user-created filter presets
    this.version(7).stores({
      catches: 'id, timestamp, species, caught, sessionId, fishingAreaId, lat, lng',
      sessions: 'id, startTime, endTime, isActive',
      areas: 'id, centerLat, centerLng, lastVisited, totalCatches',
      species: 'id, key, isCustom',
      methods: 'id, key, isCustom, createdAt',
      tackle: 'id, key, isCustom, createdAt',
      savedFilters: 'id, name, lastUsed, createdAt',
      pendingWeatherUpdates: 'id, catchId, catchTimestamp, queuedAt',
      pendingLocationUpdates: 'id, areaId, queuedAt'
    });
  }
}

// Create database instance
export const db = new FishingDatabase();

// Helper function to generate IDs
export function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Track if initialization is in progress or completed
let initializationPromise: Promise<void> | null = null;
let isInitialized = false;
let speciesInitialized = false;
let methodsInitialized = false;
let tackleInitialized = false;

// LocalStorage keys for tracking removed defaults
export const REMOVED_SPECIES_KEY = 'fishing_removed_species';
export const REMOVED_METHODS_KEY = 'fishing_removed_methods';
export const REMOVED_TACKLE_KEY = 'fishing_removed_tackle';
const INITIALIZATION_FLAG_KEY = 'fishing_db_initialized';

// Helper to get removed defaults from localStorage
function getRemovedDefaults(key: string): Set<string> {
  try {
    const stored = localStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

// Helper to mark a default as removed
export function markDefaultAsRemoved(key: string, itemKey: string): void {
  try {
    const removed = getRemovedDefaults(key);
    removed.add(itemKey);
    localStorage.setItem(key, JSON.stringify(Array.from(removed)));
  } catch (error) {
    console.error('Error marking default as removed:', error);
  }
}

// Default fishing methods (translation keys, not names!)
const DEFAULT_METHODS: Omit<FishingMethod, 'id'>[] = [
  { key: 'methodSpin', icon: '🎣', isCustom: false, createdAt: Date.now() },
  { key: 'methodBait', icon: '🪝', isCustom: false, createdAt: Date.now() },
  { key: 'methodTrolling', icon: '🚤', isCustom: false, createdAt: Date.now() },
];

// Default tackle items (translation keys, not names!)
const DEFAULT_TACKLE: Omit<Tackle, 'id'>[] = [
  { key: 'tackleWobbler', icon: '🐟', isCustom: false, createdAt: Date.now() },
  { key: 'tackleSoftbait', icon: '🪱', isCustom: false, createdAt: Date.now() },
  { key: 'tackleSpoon', icon: '✨', isCustom: false, createdAt: Date.now() },
];

// Initialize default species if table is empty or missing species
export async function ensureDefaultSpecies(): Promise<void> {
  // Skip if already initialized in this session
  if (speciesInitialized) {
    return;
  }
  
  // If already initializing, wait for that to complete
  if (initializationPromise) {
    console.log('🐟 Species initialization already in progress, waiting...');
    return initializationPromise;
  }
  
  // Start initialization
  initializationPromise = (async () => {
    try {
      // Get list of defaults that user has removed
      const removedSpecies = getRemovedDefaults(REMOVED_SPECIES_KEY);
      
      // Always check database state, don't rely on memory flag
      const allSpecies = await db.species.toArray();
      const existingKeys = new Set(allSpecies.map(s => s.key));
      
      if (allSpecies.length === 0) {
        console.log('🐟 Species table is empty, populating default species (except removed ones)...');
        for (const metadata of DEFAULT_SPECIES_METADATA) {
          // Skip if user has removed this default
          if (removedSpecies.has(metadata.key)) {
            console.log(`⏭️  Skipping removed default species: ${metadata.key}`);
            continue;
          }
          
          await db.species.add({
            id: generateId(),
            key: metadata.key,
            icon: metadata.icon,
            isCustom: false,
            isLargeSpecies: metadata.isLargeSpecies
          });
        }
        console.log('✅ Populated default species');
        speciesInitialized = true;
        return;
      }
      
      // Check for missing species (but respect user's removal choice)
      let added = 0;
      for (const metadata of DEFAULT_SPECIES_METADATA) {
        // Skip if already exists OR if user has removed it
        if (existingKeys.has(metadata.key) || removedSpecies.has(metadata.key)) {
          continue;
        }
        
        await db.species.add({
          id: generateId(),
          key: metadata.key,
          icon: metadata.icon,
          isCustom: false,
          isLargeSpecies: metadata.isLargeSpecies
        });
        added++;
      }
      
      if (added > 0) {
        console.log('✅ Added', added, 'missing default species');
      }
      
      speciesInitialized = true;
    } catch (error) {
      console.error('❌ Error ensuring default species:', error);
      initializationPromise = null; // Reset on error so it can be retried
      throw error;
    }
  })();
  
  return initializationPromise;
}

// Initialize default fishing methods
export async function ensureDefaultMethods(): Promise<void> {
  // Skip if already initialized in this session
  if (methodsInitialized) {
    return;
  }
  
  try {
    const removedMethods = getRemovedDefaults(REMOVED_METHODS_KEY);
    const allMethods = await db.methods.toArray();
    
    if (allMethods.length === 0) {
      console.log('🎣 Methods table is empty, populating default methods (except removed ones)...');
      for (const method of DEFAULT_METHODS) {
        if (removedMethods.has(method.key)) {
          console.log(`⏭️  Skipping removed default method: ${method.key}`);
          continue;
        }
        
        await db.methods.add({
          id: generateId(),
          ...method
        });
      }
      console.log('✅ Populated default methods');
      return;
    }
    
    // Check for missing defaults (but respect user's removal choice)
    const existingKeys = new Set(allMethods.filter(m => !m.isCustom).map(m => m.key));
    let added = 0;
    
    for (const method of DEFAULT_METHODS) {
      if (existingKeys.has(method.key) || removedMethods.has(method.key)) {
        continue;
      }
      
      await db.methods.add({
        id: generateId(),
        ...method
      });
      added++;
    }
    
    if (added > 0) {
      console.log('✅ Added', added, 'missing default methods');
    }
    
    methodsInitialized = true;
  } catch (error) {
    console.error('❌ Error ensuring default methods:', error);
    throw error;
  }
}

// Initialize default tackle
export async function ensureDefaultTackle(): Promise<void> {
  // Skip if already initialized in this session
  if (tackleInitialized) {
    return;
  }
  
  try {
    const removedTackle = getRemovedDefaults(REMOVED_TACKLE_KEY);
    const allTackle = await db.tackle.toArray();
    
    if (allTackle.length === 0) {
      console.log('🪝 Tackle table is empty, populating default tackle (except removed ones)...');
      for (const tackleItem of DEFAULT_TACKLE) {
        if (removedTackle.has(tackleItem.key)) {
          console.log(`⏭️  Skipping removed default tackle: ${tackleItem.key}`);
          continue;
        }
        
        await db.tackle.add({
          id: generateId(),
          ...tackleItem
        });
      }
      console.log('✅ Populated default tackle');
      return;
    }
    
    // Check for missing defaults (but respect user's removal choice)
    const existingKeys = new Set(allTackle.filter(t => !t.isCustom).map(t => t.key));
    let added = 0;
    
    for (const tackleItem of DEFAULT_TACKLE) {
      if (existingKeys.has(tackleItem.key) || removedTackle.has(tackleItem.key)) {
        continue;
      }
      
      await db.tackle.add({
        id: generateId(),
        ...tackleItem
      });
      added++;
    }
    
    if (added > 0) {
      console.log('✅ Added', added, 'missing default tackle');
    }
    
    tackleInitialized = true;
  } catch (error) {
    console.error('❌ Error ensuring default tackle:', error);
    throw error;
  }
}

// Master initialization function - call this ONCE from App.tsx
export async function initializeDatabase(): Promise<void> {
  // If already initialized, return immediately
  if (isInitialized) {
    console.log('🔄 Database already initialized, skipping...');
    return;
  }
  
  // If initialization is in progress, wait for it
  if (initializationPromise) {
    console.log('⏳ Database initialization in progress, waiting...');
    return initializationPromise;
  }
  
  // Check localStorage flag (in case page was reloaded)
  const wasInitialized = localStorage.getItem(INITIALIZATION_FLAG_KEY);
  if (wasInitialized === 'true') {
    isInitialized = true;
    console.log('✅ Database was already initialized (from localStorage)');
    // Still do a quick check for missing defaults without adding duplicates
    await ensureDefaultSpecies();
    await ensureDefaultMethods();
    await ensureDefaultTackle();
    return;
  }
  
  // Start initialization
  console.log('🚀 Starting database initialization...');
  initializationPromise = (async () => {
    try {
      await ensureDefaultSpecies();
      await ensureDefaultMethods();
      await ensureDefaultTackle();
      
      // Mark as initialized
      isInitialized = true;
      localStorage.setItem(INITIALIZATION_FLAG_KEY, 'true');
      console.log('✅ Database initialization complete!');
    } catch (error) {
      console.error('❌ Database initialization failed:', error);
      initializationPromise = null; // Reset so it can be retried
      throw error;
    }
  })();
  
  return initializationPromise;
}
